// src/pages/sow-draft/MilestonesTable.jsx
//
// Fixed-Bid milestone schedule -- a small editable grid, not a great fit
// for the read-only DataTable, so this stays a plain styled <table>.

import PropTypes from "prop-types";
import { Plus, X } from "lucide-react";
import { Button, DatePicker } from "../../shared/ui";
import { formatCurrency } from "./sowDraftHelpers";

const cellInput = "w-full text-sm px-2 py-1.5 rounded border border-line focus:outline-none focus:border-brand-600";

export default function MilestonesTable({ milestones, totals, grandTotal, onAdd, onUpdate, onRemove }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-ink">Milestones</h3>
        <Button size="sm" variant="secondary" onClick={onAdd}>
          <Plus size={14} /> Add
        </Button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-line">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left text-xs text-muted">
              <th className="p-2">Name</th>
              <th className="p-2">Deliverable</th>
              <th className="p-2 w-40">Due</th>
              <th className="p-2 w-20">%</th>
              <th className="p-2 w-28 text-right">Amount</th>
              <th className="p-2 w-10" />
            </tr>
          </thead>
          <tbody>
            {milestones.map((m, idx) => (
              <tr key={idx} className="border-t border-line">
                <td className="p-1.5">
                  <input className={cellInput} value={m.name} onChange={(e) => onUpdate(idx, "name", e.target.value)} />
                </td>
                <td className="p-1.5">
                  <input
                    className={cellInput}
                    value={m.deliverable}
                    onChange={(e) => onUpdate(idx, "deliverable", e.target.value)}
                  />
                </td>
                <td className="p-1.5">
                  <DatePicker
                    bare
                    className={cellInput}
                    value={m.dueDate}
                    onChange={(iso) => onUpdate(idx, "dueDate", iso)}
                  />
                </td>
                <td className="p-1.5">
                  <input
                    type="number"
                    className={cellInput}
                    value={m.percentage}
                    onChange={(e) => onUpdate(idx, "percentage", parseFloat(e.target.value) || 0)}
                  />
                </td>
                <td className="p-2 text-right text-ink">{formatCurrency((grandTotal * (m.percentage || 0)) / 100)}</td>
                <td className="p-1.5 text-center">
                  <button type="button" onClick={() => onRemove(idx)} className="text-red-500 hover:text-red-700">
                    <X size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-line bg-gray-50">
              <td colSpan={3} className="p-2 text-right font-semibold text-ink">
                Total:
              </td>
              <td className={`p-2 font-semibold ${totals.isValid ? "text-ink" : "text-red-600"}`}>
                {totals.total.toFixed(1)}%
              </td>
              <td className="p-2 text-right font-semibold text-ink">{formatCurrency(grandTotal)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

MilestonesTable.propTypes = {
  milestones: PropTypes.array.isRequired,
  totals: PropTypes.shape({ total: PropTypes.number, isValid: PropTypes.bool }).isRequired,
  grandTotal: PropTypes.number.isRequired,
  onAdd: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};
